import { FolderKanban, Mail, UserPlus, Vote } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDashboard } from '@/lib/api-hooks';

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function DashboardPage() {
  const { data, isLoading } = useDashboard();

  if (isLoading && !data) {
    return <p className="text-muted-foreground text-sm">Loading dashboard…</p>;
  }

  const totals = data?.totals;
  const liveVoting = data?.liveVoting ?? [];

  const stats = [
    {
      label: 'Total projects',
      value: String(totals?.projects ?? 0),
      hint: `${totals?.liveVoting ?? 0} live now`,
      icon: FolderKanban,
    },
    {
      label: 'Live voting',
      value: String(totals?.liveVoting ?? 0),
      hint: 'open for public voting',
      icon: Vote,
    },
    {
      label: 'Voter emails',
      value: (totals?.voterEmails ?? 0).toLocaleString(),
      hint: 'captured after voting',
      icon: Mail,
    },
    {
      label: 'Subscribers',
      value: (totals?.subscribers ?? 0).toLocaleString(),
      hint: 'jury + post-vote',
      icon: UserPlus,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.label}>
            <CardContent className="space-y-1">
              <div className="flex items-center justify-between">
                <p className="text-muted-foreground text-sm">{stat.label}</p>
                <stat.icon className="size-4 text-muted-foreground" />
              </div>
              <p className="font-semibold text-2xl tracking-tight">{stat.value}</p>
              <p className="text-muted-foreground text-xs">{stat.hint}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Live voting</CardTitle>
          <CardDescription>Projects currently open for public voting.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Closes</TableHead>
                <TableHead>Leading artist</TableHead>
                <TableHead className="text-right">Votes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {liveVoting.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No projects are open for voting.
                  </TableCell>
                </TableRow>
              ) : (
                liveVoting.map((row) => (
                  <TableRow key={row.projectSlug}>
                    <TableCell className="font-medium">
                      <Link to={`/projects/${row.projectSlug}`} className="hover:underline">
                        {row.project}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(row.closesAt)}
                    </TableCell>
                    <TableCell>
                      {row.leading && row.leadingSlug ? (
                        <Link to={`/artists/${row.leadingSlug}`} className="hover:underline">
                          {row.leading}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{row.votes.toLocaleString()}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
